import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, FlaskConical, Lock } from "lucide-react";
import { get, post } from "../lib/api";
import { paypalFee } from "../lib/pricing";
import { Seo } from "../lib/seo";
import { Section } from "../components/ui";

const peso = (v) => "\u20b1" + Number(v || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

let _capPromise = null;
function loadCapture(clientId) {
  if (window.ppTestCap) return Promise.resolve(window.ppTestCap);
  if (_capPromise) return _capPromise;
  _capPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=PHP&intent=capture&components=buttons`;
    s.setAttribute("data-namespace", "ppTestCap");
    s.onload = () => resolve(window.ppTestCap);
    s.onerror = () => { _capPromise = null; reject(new Error("PayPal failed to load")); };
    document.body.appendChild(s);
  });
  return _capPromise;
}
let _subPromise = null;
function loadSub(clientId) {
  if (window.ppTestSub) return Promise.resolve(window.ppTestSub);
  if (_subPromise) return _subPromise;
  _subPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=PHP&vault=true&intent=subscription&components=buttons`;
    s.setAttribute("data-namespace", "ppTestSub");
    s.onload = () => resolve(window.ppTestSub);
    s.onerror = () => { _subPromise = null; reject(new Error("PayPal failed to load")); };
    document.body.appendChild(s);
  });
  return _subPromise;
}

export default function TestPay() {
  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [business, setBusiness] = useState("");
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("bc_testpage_ok") === "1");
  const emailRef = useRef("");
  const nameRef = useRef("");
  const businessRef = useRef("");
  useEffect(() => { emailRef.current = email; }, [email]);
  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { businessRef.current = business; }, [business]);
  useEffect(() => { if (unlocked) get("/checkout/test-products").then(setCfg).catch(() => setCfg({ configured: false, products: [] })); }, [unlocked]);

  if (!unlocked) return <LockScreen onUnlock={() => { sessionStorage.setItem("bc_testpage_ok", "1"); setUnlocked(true); }} />;

  return (
    <>
      <Seo title="₱100 test checkout" path="/100-test/" description="Internal test page." noindex />
      <Section>
        <div className="max-w-2xl mx-auto" data-testid="testpay-page">
          <div className="flex items-center gap-2 text-brand-700">
            <FlaskConical className="h-5 w-5" />
            <span className="eyebrow">Internal · unlisted</span>
          </div>
          <h1 className="mt-3 text-4xl tracking-tight text-ink-950">₱100 test checkout</h1>
          <p className="mt-3 text-ink-600">
            Private page for testing the PayPal payment + provisioning flow with ₱100 items. Not linked anywhere and
            hidden from search engines. {cfg?.env && <span className="font-mono text-2xs uppercase tracking-eyebrow text-brand-700">· PayPal {cfg.env}</span>}
          </p>
          {cfg?.env === "live"
            ? <div className="mt-3 flex items-center gap-2 text-xs text-signal font-medium" data-testid="testpay-live-warning"><Lock className="h-3.5 w-3.5" /> LIVE mode — these ₱100 payments are REAL charges. Use a real card only for a genuine test.</div>
            : <div className="mt-3 flex items-center gap-2 text-xs text-ink-400"><Lock className="h-3.5 w-3.5" /> Sandbox mode — use a PayPal sandbox buyer; no real money moves.</div>}

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="eyebrow block mb-1.5">Full name</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="field w-full"
                placeholder="Jane Dela Cruz" data-testid="testpay-name" />
            </label>
            <label className="block">
              <span className="eyebrow block mb-1.5">Test buyer email (account gets provisioned here)</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="field w-full"
                placeholder="test-buyer@example.com" data-testid="testpay-email" />
            </label>
            <label className="block">
              <span className="eyebrow block mb-1.5">Business name <span className="text-ink-400 normal-case">(optional)</span></span>
              <input type="text" value={business} onChange={(e) => setBusiness(e.target.value)} className="field w-full"
                placeholder="Bloom Studio" data-testid="testpay-business" />
            </label>
          </div>

          {cfg && !cfg.configured && (
            <div className="mt-6 card-hairline p-5 text-sm text-signal" data-testid="testpay-not-configured">PayPal is not configured in this environment.</div>
          )}

          <div className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="testpay-products">
            {(cfg?.products || []).map((p) => (
              <TestProductCard key={p.id} product={p} clientId={cfg.client_id} emailRef={emailRef} nameRef={nameRef} businessRef={businessRef} />
            ))}
            {cfg && (cfg.products || []).length === 0 && cfg.configured && (
              <div className="col-span-full text-sm text-ink-500">No test products found. Seed them in Admin → PayPal Plans → Test products.</div>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}

function LockScreen({ onUnlock }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await post("/testpage/unlock", { code }); onUnlock(); }
    catch (e2) { setErr(e2?.response?.data?.detail || "Incorrect access code."); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Seo title="₱100 test checkout" path="/100-test/" description="Internal test page." noindex />
      <Section>
        <div className="max-w-sm mx-auto" data-testid="testpay-lock">
          <div className="flex items-center gap-2 text-brand-700"><Lock className="h-5 w-5" /><span className="eyebrow">Restricted</span></div>
          <h1 className="mt-3 text-3xl tracking-tight text-ink-950">Enter access code</h1>
          <p className="mt-2 text-sm text-ink-600">This ₱100 test page is private. Enter the access code to continue.</p>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input type="password" value={code} onChange={(e) => setCode(e.target.value)} autoFocus
              className="field w-full" placeholder="Access code" data-testid="testpay-code-input" />
            {err && <p className="text-signal text-xs" data-testid="testpay-lock-error">{err}</p>}
            <button type="submit" disabled={busy} className="btn-primary btn-md w-full" data-testid="testpay-unlock-btn">{busy ? "Checking…" : "Unlock"}</button>
          </form>
        </div>
      </Section>
    </>
  );
}

function TestProductCard({ product, clientId, emailRef, nameRef, businessRef }) {
  const navigate = useNavigate();
  const ref = useRef(null);
  const guardRef = useRef(false);
  const detailRef = useRef("");
  const [state, setState] = useState("loading"); // loading | ready | success | error
  const [msg, setMsg] = useState("");
  const recurring = product.kind === "subscription";
  const oneTimeFee = !recurring ? paypalFee(product.price_php) : 0;
  const oneTimeTotal = !recurring ? Math.round((product.price_php + oneTimeFee) * 100) / 100 : 0;

  useEffect(() => {
    if (!clientId) { setState("error"); setMsg("PayPal not configured."); return; }
    let buttons; let closed = false;
    const loader = recurring ? loadSub : loadCapture;
    loader(clientId).then((paypal) => {
      if (closed || !ref.current) return;
      ref.current.replaceChildren();
      const guardEmail = () => {
        const em = (emailRef.current || "").trim();
        if (!em || !em.includes("@")) { guardRef.current = true; setMsg("Enter a valid buyer email above first."); setState("error"); return null; }
        return em;
      };
      const cfg = recurring ? {
        style: { shape: "pill", layout: "vertical", label: "subscribe", height: 40 },
        createSubscription: (d, actions) => actions.subscription.create({ plan_id: product.paypal_plan_id }),
        onApprove: () => { setState("success"); setMsg("Subscription approved ✓ (activation arrives via webhook)."); },
        onError: () => { if (!closed) { setState("error"); setMsg("PayPal reported an error."); } },
      } : {
        style: { shape: "pill", layout: "vertical", label: "pay", height: 40 },
        createOrder: async () => {
          const em = guardEmail(); if (!em) throw new Error("email required");
          guardRef.current = false; detailRef.current = "";
          try {
            const r = await post("/checkout/test/create-order", { email: em, test_product_id: product.id });
            return r.paypal_order_id;
          } catch (e) {
            detailRef.current = e?.response?.data?.detail || "Could not start PayPal checkout.";
            throw e;
          }
        },
        onApprove: async (data) => {
          setState("loading"); setMsg("Confirming payment…");
          try {
            const r = await post("/checkout/test/capture-order", { email: emailRef.current.trim(), name: (nameRef?.current || "").trim(), business_name: (businessRef?.current || "").trim(), test_product_id: product.id, paypal_order_id: data.orderID });
            navigate("/welcome", { state: { token: r.onboarding_token, orderId: r.order_id, email: emailRef.current.trim(), summary: r.summary } });
          } catch (e) { setMsg(e?.response?.data?.detail || "Payment wasn't completed."); setState("error"); }
        },
        onError: () => { if (closed) return; if (guardRef.current) { guardRef.current = false; return; } setState("error"); setMsg(detailRef.current || "PayPal reported an error."); detailRef.current = ""; },
      };
      buttons = paypal.Buttons(cfg);
      if (buttons.isEligible && !buttons.isEligible()) { setState("error"); setMsg("PayPal isn't available."); return; }
      return buttons.render(ref.current).then(() => { if (!closed) setState("ready"); })
        .catch(() => { if (!closed) { setState("error"); setMsg("PayPal couldn't load."); } });
    }).catch(() => { if (!closed) { setState("error"); setMsg("PayPal couldn't load."); } });
    return () => { closed = true; try { buttons && buttons.close(); } catch (e) { /* noop */ } };
  }, [clientId, product.id, recurring]); // eslint-disable-line

  return (
    <div className="card-hairline p-5 flex flex-col" data-testid={`testpay-card-${product.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-lg text-ink-950">{product.label}</span>
        <span className="badge bg-surface-sunk text-ink-600 border border-line">{recurring ? product.interval?.toLowerCase() : product.kind}</span>
      </div>
      <div className="mt-1 font-mono text-2xl text-ink-950">{peso(product.price_php)}{recurring ? <span className="text-sm text-ink-400"> / {product.interval === "YEAR" ? "yr" : "mo"}</span> : <span className="text-sm text-ink-400"> one-time</span>}</div>
      {product.credits ? <div className="mt-1 text-xs text-ink-500">Grants {product.credits} credits</div> : <div className="mt-1 text-xs text-ink-500">{recurring ? "Recurring subscription" : product.kind === "lifetime" ? "Provisions a Pro account" : ""}</div>}
      {!recurring && <div className="mt-1 text-xs text-ink-500" data-testid={`testpay-fee-${product.id}`}>+ PayPal fee {peso(oneTimeFee)} · <span className="text-ink-700 font-medium">Total {peso(oneTimeTotal)}</span></div>}
      <div className="mt-4">
        {state === "success" ? (
          <div className="flex items-center gap-2 text-positive text-sm" data-testid={`testpay-success-${product.id}`}><CheckCircle2 className="h-5 w-5" />{msg}</div>
        ) : (
          <>
            <div ref={ref} className="min-h-[44px]" data-testid={`testpay-buttons-${product.id}`} />
            {state === "loading" && <p className="text-xs text-ink-400 mt-1">{msg || "Loading PayPal…"}</p>}
            {state === "error" && <p className="text-xs text-signal mt-1" data-testid={`testpay-error-${product.id}`}>{msg}</p>}
          </>
        )}
      </div>
    </div>
  );
}
